// The salon's own uploads, so an image uploaded once (an email logo, a
// Christmas photo) is one tap away in every image picker afterwards.
//
// Kept on the server (GET/POST marketing/images, DELETE marketing/images/:id:
// per salon, per self-employed stylist) so it follows the salon to every
// device and the whole team. localStorage stays as the cache every picker
// reads synchronously; pickers subscribe, so an upload in one shows up in any
// other that's open, and subscribing refreshes the cache from the server.
//
// The server records uploads itself (marketing/upload-image and the
// automation editor's email-body upload), so adding here only updates the
// cache. A browser that had a library before the server kept one sends it
// up once.

import axios from 'axios'
import API from '../../../config/API'

const MAX_ITEMS = 60
const EVENT = 'beu-image-library'
const REFRESH_MS = 60 * 1000

const salonOf = (salonId) => salonId || localStorage.getItem('salonId') || 'me'
const salonKey = (salonId) => `beu-image-library-${salonOf(salonId)}`
const migratedKey = (salonId) => `beu-image-library-migrated-${salonOf(salonId)}`

const headers = () => ({
  'Content-Type': 'application/json',
  'authorization': 'Basic YmV1YXBwOmJldWFwcA==',
  'accessToken': `${localStorage.getItem('accessToken')}`,
})
const signedIn = () => { try { return !!localStorage.getItem('accessToken') } catch (e) { return false } }
const payload = (res) => {
  const d = res && res.data
  if (!d || Number(d.statusCode) !== 1) throw new Error('Request failed')
  return d.responseData && d.responseData.result !== undefined ? d.responseData.result : d.responseData
}

export const loadLibrary = (salonId) => {
  try {
    const raw = JSON.parse(localStorage.getItem(salonKey(salonId)) || '[]')
    return Array.isArray(raw) ? raw.filter(i => i && typeof i.url === 'string' && /^https:/.test(i.url)) : []
  } catch (e) {
    return []
  }
}

const save = (salonId, items) => {
  try { localStorage.setItem(salonKey(salonId), JSON.stringify(items.slice(0, MAX_ITEMS))) } catch (e) { /* private mode: lasts the visit */ }
  try { window.dispatchEvent(new CustomEvent(EVENT)) } catch (e) { /* old browsers */ }
}

const fromServer = (img) => ({
  id: img.id,
  url: img.url,
  name: img.name || '',
  w: img.width || 0,
  h: img.height || 0,
  at: img.createdAt ? new Date(img.createdAt).getTime() : Date.now(),
})

// Newest first; an image already in the library moves back to the top.
// Accepts the server's upload reply ({ url, id, width, height }) as is.
export const addToLibrary = (item, salonId) => {
  if (!item || !item.url || !/^https:/.test(item.url)) return
  const rest = loadLibrary(salonId).filter(i => i.url !== item.url)
  save(salonId, [{
    ...(item.id ? { id: item.id } : {}),
    url: item.url,
    name: item.name || '',
    w: item.w || item.width || 0,
    h: item.h || item.height || 0,
    at: Date.now(),
  }, ...rest])
}

export const removeFromLibrary = (url, salonId) => {
  const items = loadLibrary(salonId)
  const gone = items.find(i => i.url === url)
  save(salonId, items.filter(i => i.url !== url))
  if (gone && gone.id && signedIn()) {
    axios.delete(`${API.MARKETING_IMAGES}/${encodeURIComponent(gone.id)}`, { headers: headers() }).catch(() => {})
  }
}

// One-time: send up what this browser collected before the server kept a
// library. The server only accepts images already in Beu's own storage.
const migrate = async (salonId) => {
  try { if (localStorage.getItem(migratedKey(salonId)) === '1') return [] } catch (e) { return [] }
  const local = loadLibrary(salonId).filter(i => !i.id)
  const refused = []
  // Oldest first so the server keeps the same order.
  for (const item of [...local].reverse()) {
    try {
      await axios.post(API.MARKETING_IMAGES, {
        url: item.url,
        name: item.name || '',
        width: item.w || 0,
        height: item.h || 0,
        createdAt: item.at ? new Date(item.at).toISOString() : undefined,
      }, { headers: headers() }).then(payload)
    } catch (e) {
      refused.push(item)
    }
  }
  try { localStorage.setItem(migratedKey(salonId), '1') } catch (e) { /* private mode */ }
  return refused
}

const inflight = {}
const lastRefresh = {}
// Bring the cache in line with the server. Images the server refused during
// migration stay on this device only (they were never uploaded through Beu).
export const refreshLibrary = (salonId, { force = false } = {}) => {
  const key = salonOf(salonId)
  if (!signedIn() || !API.MARKETING_IMAGES) return Promise.resolve(loadLibrary(salonId))
  if (inflight[key]) return inflight[key]
  if (!force && lastRefresh[key] && Date.now() - lastRefresh[key] < REFRESH_MS) return Promise.resolve(loadLibrary(salonId))
  lastRefresh[key] = Date.now()
  const fetchList = () => axios.get(`${API.MARKETING_IMAGES}?limit=${MAX_ITEMS}`, { headers: headers() })
    .then(payload).then((res) => ((res && res.images) || []).map(fromServer))
  inflight[key] = (async () => {
    try {
      // List first: it proves the server keeps a library before anything
      // is migrated to it
      let server = await fetchList()
      const pending = loadLibrary(salonId).filter(i => !i.id && !i.localOnly).length
      const localOnly = await migrate(salonId)
      if (pending > localOnly.length) server = await fetchList()
      const urls = new Set(server.map(i => i.url))
      const keep = [...localOnly, ...loadLibrary(salonId).filter(i => i.localOnly)]
        .filter(i => !urls.has(i.url))
        .map(i => ({ ...i, localOnly: true }))
      const merged = [...server, ...keep].sort((a, b) => (b.at || 0) - (a.at || 0))
      save(salonId, merged)
      return merged
    } catch (e) {
      // Offline or an older server: the cache is still the library.
      return loadLibrary(salonId)
    } finally {
      delete inflight[key]
    }
  })()
  return inflight[key]
}

export const subscribeLibrary = (fn) => {
  const onChange = () => fn()
  const onStorage = (e) => { if (e.key && e.key.indexOf('beu-image-library-') === 0) fn() }
  window.addEventListener(EVENT, onChange)
  window.addEventListener('storage', onStorage)
  // A picker opening is the moment to catch up with other devices
  refreshLibrary().catch(() => {})
  return () => {
    window.removeEventListener(EVENT, onChange)
    window.removeEventListener('storage', onStorage)
  }
}
